import { useState } from "react";
import { renameResourceFolder, deleteResourceFolder } from "../../lib/api";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Folder, FolderOpen, MoreHorizontal, Megaphone, Pencil, Trash2, Upload } from "lucide-react";

export const ResourceFolderSection = ({ folder, items, isAdmin, renderCard, onChanged, onUpload, onAnnounce, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = open ? FolderOpen : Folder;
  const tid = folder?.id || "unfiled";

  const rename = async () => {
    const name = window.prompt("Rename folder", folder.name);
    if (!name?.trim()) return;
    try {
      await renameResourceFolder(folder.id, name.trim());
      toast.success("Folder renamed");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not rename"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${folder.name}"? Its files move to Unfiled.`)) return;
    try {
      await deleteResourceFolder(folder.id);
      toast.success("Folder deleted");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete folder"); }
  };

  return (
    <div className="rounded-xl border bg-card" data-testid={`resource-folder-${tid}`}>
      <div className="flex items-center gap-2 px-4 py-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 text-left"
          data-testid={`resource-folder-toggle-${tid}`}>
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Icon className="h-5 w-5 text-amber-500" />
          <span className="font-semibold text-sm">{folder?.name || "Unfiled"}</span>
          <span className="text-xs bg-muted px-1.5 rounded">{items.length}</span>
        </button>
        {isAdmin && folder && <>
          <Button size="sm" variant="ghost" onClick={() => onUpload(folder.id)} data-testid={`resource-folder-upload-${tid}`}>
            <Upload className="h-4 w-4 mr-1" />Upload</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`resource-folder-menu-${tid}`}><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAnnounce(folder)} data-testid={`resource-folder-announce-${tid}`}><Megaphone className="h-4 w-4 mr-2" />Notify partners</DropdownMenuItem>
              <DropdownMenuItem onClick={rename} data-testid={`resource-folder-rename-${tid}`}><Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
              <DropdownMenuItem onClick={remove} className="text-red-600" data-testid={`resource-folder-delete-${tid}`}><Trash2 className="h-4 w-4 mr-2" />Delete folder</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>}
      </div>
      {open && (
        <div className="px-4 pb-4">
          {items.length === 0
            ? <p className="text-xs text-muted-foreground py-4 text-center">This folder is empty.</p>
            : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{items.map(renderCard)}</div>}
        </div>
      )}
    </div>
  );
};
